import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import {
  CONSENT_DENIED,
  CONSENT_GRANTED,
  CONSENT_REOPEN_EVENT,
  getConsent,
  needsDecision,
  setConsent,
} from '../config/consent';
import { isConsentGeoGateEnabled } from '../config/features';
import { clearAnalyticsSession } from '../utils/analytics';

// Consent gate for Google Tag Manager.
//
// Two independent questions, in order:
//   1. Has this visitor already decided?  -> honour it, never ask again.
//   2. Does this visitor need to be asked? -> the geo gate, when enabled.
//
// With REACT_APP_CONSENT_GEO_GATE off (the default) question 2 is skipped
// entirely and everyone is asked. With it on, only visitors the edge places in
// the UK/EEA are asked; everyone else is treated as before. The region lookup
// fails closed to 'uk_eu', so a broken or blocked lookup asks rather than
// tracks. See supabase/functions/consent-region/index.ts.

const REGION_ENDPOINT = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/consent-region`;

// Kong rejects a function call with no anon key before the function runs, so
// without these the gate would 401 on every request, fail closed, and show the
// banner to everyone -- looking exactly like a geo lookup that decided
// "consent required" rather than one that never ran. Same shape as
// src/services/privateTransferRoutePreviewApi.js.
const REGION_HEADERS = {
  apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
  Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
};

function loadGtm() {
  if (typeof window.__rsLoadGtm === 'function') window.__rsLoadGtm();
}

/**
 * Does this visitor need to be asked? Resolves true when the gate is off, and
 * on any failure when it is on.
 */
async function shouldAsk() {
  if (!isConsentGeoGateEnabled()) return true;

  try {
    const res = await fetch(REGION_ENDPOINT, { method: 'GET', headers: REGION_HEADERS });
    if (!res.ok) return true;

    const body = await res.json();
    return body?.region !== 'other';
  } catch {
    // Offline, blocked by an extension, function down: ask.
    return true;
  }
}

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // A decision already on record is final until the visitor reopens it.
    if (!needsDecision()) {
      if (getConsent()?.status === CONSENT_GRANTED) loadGtm();
      return undefined;
    }

    shouldAsk().then((ask) => {
      if (cancelled) return;
      if (ask) {
        setVisible(true);
      } else {
        // Outside the consent regimes: behaves as it did before this gate
        // existed. No banner, and analytics loads.
        loadGtm();
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // The footer's "Cookie settings" clears the decision and fires this, so the
  // banner is reachable even for visitors the geo gate would never have asked.
  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  const decide = useCallback((status) => {
    setConsent(status);

    if (status === CONSENT_DENIED) {
      // Otherwise accept -> decline -> accept resumes the original session id
      // and relinks the new activity to everything done before the refusal.
      clearAnalyticsSession();
    }

    if (status === CONSENT_GRANTED) {
      loadGtm();
      setVisible(false);
      return;
    }

    // Withdrawing consent on a page where GTM is already running. The container
    // cannot be unloaded -- it has listeners and timers attached and may hold
    // cookies -- so the only honest way to stop it is to reload, which starts
    // the page over with the decision now recorded as denied. Only reaches here
    // via the footer's "Cookie settings" on an accepted session; a first-time
    // Decline never loaded anything and just closes the banner.
    if (window.__rsGtmLoaded) {
      window.location.reload();
      return;
    }

    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      className="fixed inset-x-0 bottom-0 z-50 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0"
    >
      <div className="mx-auto max-w-4xl m-3 md:m-4 rounded-2xl border border-[#E8DED1] bg-white shadow-lg p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="hidden sm:flex w-10 h-10 shrink-0 bg-[#A44432] rounded-xl items-center justify-center">
              <Cookie className="w-5 h-5 text-white" />
            </div>
            <div className="text-sm text-[#2D1E17] leading-relaxed">
              <h2 id="cookie-banner-title" className="font-serif font-bold text-base mb-1">
                Cookies on this site
              </h2>
              <p className="text-[#7A6E67]">
                We use cookies to measure how people find and book their travel, including
                which of our Google ads lead to bookings, and to show our ads to past visitors.
                They are optional — booking works exactly the same either way. See our{' '}
                <Link to="/privacy" className="text-[#A44432] font-medium hover:underline">
                  privacy policy
                </Link>
                .
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => decide(CONSENT_DENIED)}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-[#E8DED1] text-sm font-medium text-[#2D1E17] hover:bg-[#FAF3E0] transition-colors"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={() => decide(CONSENT_GRANTED)}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-[#A44432] text-sm font-medium text-white hover:bg-[#8d3a2b] transition-colors"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
